import { CASCADE_ENUM } from '../../../../services/enum.service';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import {
  CONTRACT_TYPE as CONTRACT_TYPE_ENUM,
  ICONS,
  ICON_COLORS,
  PRODUCT_TYPE,
  CONTRACT_TYPE,
} from '../../../../utils/enums/generic.enum';
import {
  getFormattedDate,
  getFormattedNumber,
  getPriceRange,
  getRange,
  getStatusColour,
  getStatusTranslate,
  getPriceValue,
  getCreditProfileValue,
  DEFAULT_REC_SETTLEMENT_POINT,
} from '../../../../utils/formatting';
import {
  CUSTOM_HEADER_TOOLTIPS,
  filterGreaterOrEqualNumeric,
  filterLessOrEqualNumeric,
  filterNumericRange,
  headerWithTooltipColParams,
} from '../../../../utils/grid';
import type { RfqListing } from './rfq-listing';

export enum COLUMN_FIELDS {
  TICKER = 'TICKER',
  UNIQUE_ID = 'UNIQUE_ID',
  TECHNOLOGY = 'TECHNOLOGY',
  ISO = 'ISO',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  VOLUME = 'REQUESTED_VOLUME',
  FIXED_PRICE = 'FIXED_PRICE',
  START_DATE = 'TENOR_START_DATE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  COLLATERAL = 'BUYER_POST_COD_COLLATERAL',
  IMPLIED_CARBON_PRICE = 'IMPLIED_CARBON_PRICE',
  STATUS = 'RFQ_STATUS',
  CONTRACT_TYPE = 'CONTRACT_TYPE',
  APPLICABLE_REC_PROGRAM = 'RFQ_APPLICABLE_REC_PROGRAM',
  EXPECTED_SETTLEMENT = 'EXPECTED_SETTLEMENT',
  DOWNSIDE_RISK = 'DOWNSIDE_RISK',
  NODAL_LME_HISTORIC = 'NODAL_LME_HISTORIC',
  SETTLEMENT_POINT_INDEX = 'SETTLEMENT_INDEX_STRING',
  NEGATIVE_PRICING_METHOD = 'RFQ_NEGATIVE_PRICING_METHOD',
  PERFORMANCE_GUARANTEE_METHOD = 'PERFORMANCE_GUARANTEE_METHOD',
}

function getStatusStyle(params: any) {
  const response = {};

  if (params.data?.RFQ_STATUS) {
    response['color'] = getStatusColour(params.data?.RFQ_STATUS);
  }

  return response;
}

export function RFQ_LISTING_COLUMNS(context: RfqListing) {
  return [
    {
      headerName: '',
      suppressMenu: true,
      checkboxSelection: (params) => {
        if (context.selectedRfqs.find((elm) => elm.RFQ_ID === params.data.RFQ_ID)) {
          params.node.setSelected(true);
        }
        return context.allowRfqSelection;
      },
      maxWidth: '70',
      lockPosition: 'left',
      lockPinned: true,
      suppressColumnsToolPanel: true,
      rowDrag: true,
    },
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      valueGetter: (params) => {
        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];

        if (params.data.CONTRACT_TYPE === CONTRACT_TYPE_ENUM.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
          iconList.push({
            icon: ICONS.AUTO_EXECUTED_ICON,
            width: '16px',
            height: '16px',
            fill: ICON_COLORS.AUTO_EXECUTED_STATUS,
          });
        }

        return JSON.stringify({
          NAME: params.data.TICKER,
          ICON_LIST: iconList,
        });
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        if (!params.value && !params.data) return;

        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj?.NAME,
          id: '',
          vppaId: null,
          iconList: obj?.ICON_LIST,
        };

        return hyperlinkParams;
      },
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique Id',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.TECHNOLOGY,
      headerName: 'Technology',
      sortable: true,
      filter: 'agSetColumnFilter',
      valueGetter: (params) =>
        context.enumService.getEnumTranslation(CASCADE_ENUM.TECHNOLOGY, params.data.TECHNOLOGY),
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      valueFormatter: (params) =>
        params.data.CONTRACT_TYPE === CONTRACT_TYPE.REC
          ? DEFAULT_REC_SETTLEMENT_POINT
          : params.data.SETTLEMENT_POINT,
      filterValueGetter: (params) =>
        params.data.CONTRACT_TYPE === CONTRACT_TYPE.REC
          ? DEFAULT_REC_SETTLEMENT_POINT
          : params.data.SETTLEMENT_POINT,
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.VOLUME,
      headerName: 'Volume (MWh/year)',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      valueFormatter: (params) => getFormattedNumber(params.data.REQUESTED_VOLUME),
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.FIXED_PRICE,
      headerName: 'Fixed Price Range',
      sortable: true,
      filter: 'agNumberColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      filterParams: {
        filterOptions: [
          {
            displayKey: 'lessThanOrEqual',
            displayName: '<= Upper Bound',
            predicate: ([fv1], cellValue) => filterLessOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'greaterThanOrEqual',
            displayName: '>= Lower Bound',
            predicate: ([fv1], cellValue) => filterGreaterOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'inRange',
            displayName: 'In range',
            predicate: ([fv1, fv2], cellValue) => filterNumericRange([fv1, fv2], cellValue),
            numberOfInputs: 2,
          },
        ],
        suppressAndOrCondition: true,
      },
      valueGetter: (params) => {
        if (params.data.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
          return [params.data.FIXED_PRICE, params.data.FIXED_PRICE];
        }
        return [params.data.FIXED_PRICE_LOWER_BOUND, params.data.FIXED_PRICE_UPPER_BOUND];
      },
      valueFormatter: (params) => {
        if (params.data.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
          return getPriceValue(params.data.FIXED_PRICE, '', '$');
        }
        return getPriceRange(
          params.data.FIXED_PRICE_LOWER_BOUND,
          params.data.FIXED_PRICE_UPPER_BOUND,
        );
      },
    },
    {
      field: COLUMN_FIELDS.START_DATE,
      headerName: 'Start Date',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      valueGetter: (params) => getFormattedDate(params.data.TENOR_START_DATE),
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      sortable: true,
      headerName: 'Tenor (mo)',
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.COLLATERAL,
      headerName: 'Credit Profile ($/MW)',
      sortable: false,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      valueGetter: (params) =>
        getCreditProfileValue(
          params.data.RFQ_CREDIT_PROFILE,
          params.data.BUYER_POST_COD_COLLATERAL,
        ),
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.IMPLIED_CARBON_PRICE,
      headerName: 'Implied Carbon Price Range',
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (params) =>
        getPriceRange(params.data.IMPLIED_CARBON_PRICE_MIN, params.data.IMPLIED_CARBON_PRICE_MAX),
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      filterParams: {
        filterOptions: [
          {
            displayKey: 'lessThanOrEqual',
            displayName: '<= Upper Bound',
            predicate: ([fv1], cellValue) => filterLessOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'greaterThanOrEqual',
            displayName: '>= Lower Bound',
            predicate: ([fv1], cellValue) => filterGreaterOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'inRange',
            displayName: 'In range',
            predicate: ([fv1, fv2], cellValue) => filterNumericRange([fv1, fv2], cellValue),
            numberOfInputs: 2,
          },
        ],
        suppressAndOrCondition: true,
      },
    },
    {
      field: COLUMN_FIELDS.STATUS,
      headerName: 'Status',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      cellStyle: (params) => getStatusStyle(params),
      filterValueGetter: (params) => getStatusTranslate(params.data.RFQ_STATUS),
      valueGetter: (params) => getStatusTranslate(params.data.RFQ_STATUS),
    },
    {
      field: COLUMN_FIELDS.CONTRACT_TYPE,
      headerName: 'Contract Type',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      valueGetter: (params) =>
        context.enumService.getEnumTranslation(
          CASCADE_ENUM.CONTRACT_TYPE,
          params.data.CONTRACT_TYPE,
        ),
      filterValueGetter: (params) =>
        context.enumService.getEnumTranslation(
          CASCADE_ENUM.CONTRACT_TYPE,
          params.data.CONTRACT_TYPE,
        ),
    },
    {
      field: COLUMN_FIELDS.APPLICABLE_REC_PROGRAM,
      headerName: 'Applicable REC Program',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      filterValueGetter: (params) =>
        context.enumService.getEnumTranslation(
          CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM,
          params.data.RFQ_APPLICABLE_REC_PROGRAM,
        ),
      valueGetter: (params) =>
        context.enumService.getEnumTranslation(
          CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM,
          params.data.RFQ_APPLICABLE_REC_PROGRAM,
        ),
    },
    {
      field: COLUMN_FIELDS.EXPECTED_SETTLEMENT,
      headerName: 'Expected Settlement Range Desired ($/MWh)',
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (params) =>
        getPriceRange(params.data.EXPECTED_SETTLEMENT_MIN, params.data.EXPECTED_SETTLEMENT_MAX),
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      filterParams: {
        filterOptions: [
          {
            displayKey: 'lessThanOrEqual',
            displayName: '<= Upper Bound',
            predicate: ([fv1], cellValue) => filterLessOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'greaterThanOrEqual',
            displayName: '>= Lower Bound',
            predicate: ([fv1], cellValue) => filterGreaterOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'inRange',
            displayName: 'In range',
            predicate: ([fv1, fv2], cellValue) => filterNumericRange([fv1, fv2], cellValue),
            numberOfInputs: 2,
          },
        ],
        suppressAndOrCondition: true,
      },
    },
    {
      field: COLUMN_FIELDS.DOWNSIDE_RISK,
      headerName: 'Downside Risk Range Desired ($/MWh)',
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (params) =>
        getPriceRange(params.data.DOWNSIDE_RISK_MIN, params.data.DOWNSIDE_RISK_MAX),
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      filterParams: {
        filterOptions: [
          {
            displayKey: 'lessThanOrEqual',
            displayName: '<= Upper Bound',
            predicate: ([fv1], cellValue) => filterLessOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'greaterThanOrEqual',
            displayName: '>= Lower Bound',
            predicate: ([fv1], cellValue) => filterGreaterOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'inRange',
            displayName: 'In range',
            predicate: ([fv1, fv2], cellValue) => filterNumericRange([fv1, fv2], cellValue),
            numberOfInputs: 2,
          },
        ],
        suppressAndOrCondition: true,
      },
    },
    {
      field: COLUMN_FIELDS.NODAL_LME_HISTORIC,
      headerName: 'Historical Nodal LME Range Desired (MT CO₂/MWh)',
      sortable: true,
      filter: 'agNumberColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      valueGetter: (params) => {
        return getRange(params.data.NODAL_LME_HISTORIC_MIN, params.data.NODAL_LME_HISTORIC_MAX);
      },
      filterParams: {
        filterOptions: [
          {
            displayKey: 'lessThanOrEqual',
            displayName: '<= Upper Bound',
            predicate: ([fv1], cellValue) => filterLessOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'greaterThanOrEqual',
            displayName: '>= Lower Bound',
            predicate: ([fv1], cellValue) => filterGreaterOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'inRange',
            displayName: 'In range',
            predicate: ([fv1, fv2], cellValue) => filterNumericRange([fv1, fv2], cellValue),
            numberOfInputs: 2,
          },
        ],
        suppressAndOrCondition: true,
      },
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT_INDEX,
      headerName: 'Settlement Point Index',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      valueFormatter: (params) =>
        params.data.CONTRACT_TYPE === CONTRACT_TYPE.REC
          ? DEFAULT_REC_SETTLEMENT_POINT
          : context.enumService.getEnumTranslation(
              CASCADE_ENUM.SETTLEMENT_INDEX,
              params.data.SETTLEMENT_INDEX_STRING,
            ),
      filterValueGetter: (params) =>
        params.data.CONTRACT_TYPE === CONTRACT_TYPE.REC
          ? DEFAULT_REC_SETTLEMENT_POINT
          : context.enumService.getEnumTranslation(
              CASCADE_ENUM.SETTLEMENT_INDEX,
              params.data.SETTLEMENT_INDEX_STRING,
            ),
    },
    {
      field: COLUMN_FIELDS.NEGATIVE_PRICING_METHOD,
      headerName: 'Negative Pricing Method',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      valueGetter: (params) => {
        return context.enumService.getEnumTranslation(
          CASCADE_ENUM.RFQ_NEGATIVE_PRICING_METHOD,
          params.data.RFQ_NEGATIVE_PRICING_METHOD,
        );
      },
    },
    {
      field: COLUMN_FIELDS.PERFORMANCE_GUARANTEE_METHOD,
      headerName: 'Performance Guarantee Method',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      flex: 1,
      valueGetter: (params) =>
        context.enumService.getEnumTranslation(
          CASCADE_ENUM.RFQ_PERFORMANCE_GUARANTEE_METHOD,
          params.data.RFQ_PERFORMANCE_GUARANTEE_METHOD,
        ),
    },
  ];
}
